/**
 * Centralized date and text formatting utilities
 * Eliminates duplicate formatDate, formatDateTime functions across components
 *
 * EC-31: All times include timezone abbreviation
 * EC-32: Internal users see ET, external users see local timezone
 */

import { getDisplayTimezone } from './timezone';
import { getZoneAbbreviation } from '../config/timezones';
import { parseDateOnly } from './dateHelpers';

/**
 * MDF-201: Render a time with a short zone code that matches the rest of the app.
 *
 * `timeZoneName: 'shortGeneric'` returns a real abbreviation for US zones ("ET",
 * "PT") but a phrase for most others: "India Time", "Japan Time", "United
 * Kingdom Time", "Perth Time". Cohort time slot labels use a curated code
 * (`IST`, `JST`, `UK`), so the same zone read two different ways on one screen.
 *
 * Prefer the curated code and fall back to `shortGeneric` for any zone the
 * curated map does not cover. US zones are unaffected, because the curated code
 * and `shortGeneric` already agree for them.
 */
function formatTimeWithZoneCode(d: Date, tz: string): string {
  const time = d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: tz,
  });
  return `${time} ${getZoneCode(tz, d)}`;
}

/**
 * MED-1389 (QA round 3): the one short zone name for a time, so every screen
 * says "ET" and none says "EDT". The curated code first; for a zone the map
 * does not cover, Intl's `shortGeneric` name. No `tz` means the browser zone.
 */
export function getZoneCode(tz: string | null | undefined, at: Date): string {
  const zone = tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
  return (
    getZoneAbbreviation(zone) ??
    new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'shortGeneric' })
      .formatToParts(at)
      .find(p => p.type === 'timeZoneName')?.value ??
    ''
  );
}

/**
 * Format a date string for display (date only, no time/timezone)
 * @param timezone - IANA timezone string. Defaults to role-based timezone via getDisplayTimezone()
 * @example formatDate('2024-01-15') // "Jan 15, 2024"
 */
export function formatDate(date: string | Date | null | undefined, timezone?: string): string {
  if (!date) return '—';

  // Date-only strings (YYYY-MM-DD) must be parsed as local to avoid UTC timezone shift
  const isDateOnly = typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date);
  let d: Date;
  if (isDateOnly) {
    const [year, month, day] = date.split('-').map(Number);
    d = new Date(year, month - 1, day);
  } else {
    d = typeof date === 'string' ? new Date(date) : date;
  }
  if (isNaN(d.getTime())) return '—';

  // For date-only strings, skip timezone conversion (already local)
  if (isDateOnly) {
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  const tz = timezone || getDisplayTimezone();

  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: tz,
  });
}

/**
 * Format a DATE-ONLY value (SQL DATE column) for display — never timezone-converts.
 *
 * MED-1265: the API returns DATE columns as full UTC-midnight ISO timestamps
 * ("2026-07-28T00:00:00.000Z"), which formatDate() timezone-converts — rolling
 * the calendar date back a day for any viewer west of UTC. Use THIS for values
 * backed by a SQL DATE column (due dates, cohort start/end, birthdays, payment
 * dates); use formatDate/formatDateTime for genuine datetimes, which must keep
 * converting to the viewer's timezone.
 *
 * @example formatDateOnly('2026-07-28') // "Jul 28, 2026"
 * @example formatDateOnly('2026-07-28T00:00:00.000Z') // "Jul 28, 2026" (not Jul 27)
 */
export function formatDateOnly(date: string | Date | null | undefined): string {
  if (!date) return '—';
  const d =
    typeof date === 'string'
      ? parseDateOnly(date)
      : new Date(date.getFullYear(), date.getMonth(), date.getDate());
  if (!d || isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format a DATE-ONLY range (SQL DATE columns) — never timezone-converts.
 * Same calendar-date guarantee as formatDateOnly (MED-1265).
 * @example formatDateOnlyRange('2024-01-15', '2024-03-15') // "Jan 15 - Mar 15, 2024"
 */
export function formatDateOnlyRange(
  startDate: string | Date | null | undefined,
  endDate: string | Date | null | undefined,
): string {
  if (!startDate && !endDate) return '—';
  if (!startDate) return `Until ${formatDateOnly(endDate)}`;
  if (!endDate) return `From ${formatDateOnly(startDate)}`;

  const start =
    typeof startDate === 'string'
      ? parseDateOnly(startDate)
      : new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  const end =
    typeof endDate === 'string'
      ? parseDateOnly(endDate)
      : new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

  if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) return '—';

  const sameYear = start.getFullYear() === end.getFullYear();

  const startStr = start.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: sameYear ? undefined : 'numeric',
  });

  const endStr = end.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return `${startStr} - ${endStr}`;
}

/**
 * Format a time string for display with timezone abbreviation
 * @param timezone - IANA timezone string. Defaults to role-based timezone (ET for admin, local for coach)
 * @example formatTime('2024-01-15T14:30:00Z') // "9:30 AM ET" (admin) or "9:30 AM ET" (if in ET)
 */
export function formatTime(date: string | Date | null | undefined, timezone?: string): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '—';

  const tz = timezone || getDisplayTimezone();

  return formatTimeWithZoneCode(d, tz);
}

/**
 * Format a date string with time and timezone abbreviation
 * @param timezone - IANA timezone string. Defaults to role-based timezone
 * @example formatDateTime('2024-01-15T14:30:00Z') // "Mon, Jan 15, 2024 at 9:30 AM ET" (admin)
 */
export function formatDateTime(date: string | Date | null | undefined, timezone?: string): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '—';

  const tz = timezone || getDisplayTimezone();

  const dateStr = d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: tz,
  });

  const timeStr = formatTimeWithZoneCode(d, tz);

  return `${dateStr} at ${timeStr}`;
}

/**
 * Format a time range with timezone abbreviation (e.g., "2:00 PM - 3:00 PM ET")
 * @param timezone - IANA timezone string. Defaults to role-based timezone
 */
export function formatTimeRange(
  startDate: string | Date | null | undefined,
  endDate: string | Date | null | undefined,
  timezone?: string,
): string {
  if (!startDate || !endDate) return '—';
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate;
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return '—';

  const tz = timezone || getDisplayTimezone();

  const startTime = start.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: tz,
  });

  const endTime = formatTimeWithZoneCode(end, tz);

  return `${startTime} - ${endTime}`;
}

/**
 * Format a date range for display (date only, no time/timezone)
 * @example formatDateRange('2024-01-15', '2024-03-15') // "Jan 15 - Mar 15, 2024"
 */
export function formatDateRange(
  startDate: string | Date | null | undefined,
  endDate: string | Date | null | undefined,
): string {
  if (!startDate && !endDate) return '—';
  if (!startDate) return `Until ${formatDate(endDate)}`;
  if (!endDate) return `From ${formatDate(startDate)}`;

  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate;

  if (isNaN(start.getTime()) || isNaN(end.getTime())) return '—';

  const sameYear = start.getFullYear() === end.getFullYear();

  const startStr = start.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: sameYear ? undefined : 'numeric',
  });

  const endStr = end.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return `${startStr} - ${endStr}`;
}

/**
 * Format a date as relative time (e.g., "2 hours ago", "3 days ago")
 * @example formatRelativeTime('2024-01-15T14:30:00') // "3 days ago"
 */
export function formatRelativeTime(date: string | Date | null | undefined): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '—';

  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  const diffWeek = Math.floor(diffDay / 7);
  const diffMonth = Math.floor(diffDay / 30);
  const diffYear = Math.floor(diffDay / 365);

  if (diffSec < 60) {
    return 'just now';
  } else if (diffMin < 60) {
    return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  } else if (diffHour < 24) {
    return `${diffHour} hour${diffHour === 1 ? '' : 's'} ago`;
  } else if (diffDay < 7) {
    return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  } else if (diffWeek < 4) {
    return `${diffWeek} week${diffWeek === 1 ? '' : 's'} ago`;
  } else if (diffMonth < 12) {
    return `${diffMonth} month${diffMonth === 1 ? '' : 's'} ago`;
  } else {
    return `${diffYear} year${diffYear === 1 ? '' : 's'} ago`;
  }
}

/**
 * Format a time string (HH:MM or HH:MM:SS) to 12-hour display
 * @example formatTimeString('14:30') // "2:30 PM"
 */
export function formatTimeString(timeStr: string): string {
  const [hours, minutes] = timeStr.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
}

/**
 * Format a day-of-week string to a capitalized plural display name
 * @example formatDayOfWeek('monday') // "Mondays"
 */
export function formatDayOfWeek(day: string): string {
  return day.charAt(0).toUpperCase() + day.slice(1) + 's';
}

/**
 * Natural sort comparator for strings containing numbers.
 * Sorts "Group 1", "Group 2", ..., "Group 10" correctly instead of alphabetically.
 * Falls back to case-insensitive alphabetical for non-numeric strings.
 * @example ['Group 10', 'Group 2', 'Group 1'].sort(naturalSortCompare) // ['Group 1', 'Group 2', 'Group 10']
 */
const naturalCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

export function naturalSortCompare(a: string, b: string): number {
  return naturalCollator.compare(a, b);
}

/**
 * Format cents as USD currency string.
 * @example formatCents(17560 * 100) // "$17,560.00"
 * @example formatCents(null) // "$0.00"
 */
export function formatCents(cents: number | null | undefined): string {
  const value = (cents ?? 0) / 100;
  return `$${value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Format cents as compact USD without decimals (for large display values).
 * @example formatCentsCompact(1756000) // "$17,560"
 */
export function formatCentsCompact(cents: number | null | undefined): string {
  const value = Math.round((cents ?? 0) / 100);
  return `$${value.toLocaleString('en-US')}`;
}
